/* Dougherty Theaters: showtimes, coming soon and movie pages fed from Veezi.
   Data comes from the Cloudflare feed (settings.dt_feed_url), never from
   Veezi directly, so no tokens are ever in the browser. */
(function () {
  const TZ = 'America/Chicago';
  const THEATER_NAMES = { westside: 'Westside Cinema', stadium: 'The Stadium Theater' };
  const feeds = {};

  function getFeed(base, theater) {
    const url = base.replace(/\/$/, '') + '/feed?theater=' + encodeURIComponent(theater);
    if (!feeds[url]) {
      feeds[url] = fetch(url).then(function (r) {
        if (!r.ok) throw new Error('Feed returned ' + r.status);
        return r.json();
      });
    }
    return feeds[url];
  }

  function el(tag, attrs, children) {
    const node = document.createElement(tag);
    Object.entries(attrs || {}).forEach(function ([k, v]) {
      if (v === null || v === undefined || v === false) return;
      if (k === 'text') node.textContent = v;
      else node.setAttribute(k, v);
    });
    (children || []).forEach(function (c) { if (c) node.appendChild(c); });
    return node;
  }

  function dayKey(iso) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
  }
  function timeLabel(iso) {
    return new Intl.DateTimeFormat('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
  }
  function dayLabel(key, todayKey) {
    const d = new Date(key + 'T12:00:00');
    if (key === todayKey) return { top: 'TODAY', bottom: d.getDate() };
    return {
      top: d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
      bottom: d.getDate(),
    };
  }
  function longDay(key, todayKey) {
    if (key === todayKey) return 'Today';
    return new Date(key + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  }
  function runtime(mins) {
    if (!mins) return '';
    return Math.floor(mins / 60) + 'h ' + (mins % 60) + 'm';
  }
  function openingLabel(iso) {
    if (!iso) return '';
    return 'Opens ' + new Date(iso).toLocaleDateString('en-US', { timeZone: TZ, month: 'long', day: 'numeric' });
  }
  function titleSet(text) {
    return new Set(String(text || '').split(/\r?\n/).map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean));
  }
  function whereLabel(theaters) {
    if (theaters.length > 1) return 'Both theaters';
    return { westside: 'Litchfield only', stadium: 'Jerseyville only' }[theaters[0]] || '';
  }
  function youtubeId(url) {
    if (!url) return null;
    const m = String(url).match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
    return m ? m[1] : null;
  }
  function moviePageUrl(base, theater, film) {
    return base + (base.indexOf('?') === -1 ? '?' : '&') + 'view=movie&theater=' + encodeURIComponent(theater) + '&film=' + encodeURIComponent(film.id);
  }
  function poster(film, href) {
    const empty = function () { return el('span', { class: 'dt-poster__empty', text: film.title }); };
    let img = empty();
    if (film.poster) {
      img = el('img', { src: film.poster, alt: film.title + ' poster', loading: 'lazy' });
      img.addEventListener('error', function () { img.replaceWith(empty()); });
    }
    return el(href ? 'a' : 'div', { class: 'dt-poster', href: href }, [img]);
  }
  function message(root, text) {
    root.replaceChildren(el('p', { class: 'dt-message', text: text }));
  }

  class DtShowtimes extends HTMLElement {
    connectedCallback() {
      const base = this.dataset.feed;
      const mode = this.dataset.mode || 'now';
      const params = new URLSearchParams(location.search);
      const theaters = (mode === 'movie' && params.get('theater') ? params.get('theater') : this.dataset.theater || 'westside')
        .split(',').map(function (t) { return t.trim(); }).filter(function (t) { return THEATER_NAMES[t]; });

      if (!base) { message(this, 'Showtimes are being set up. Please call the theater for times.'); return; }
      if (theaters.length === 1) {
        try { localStorage.setItem('dt_theater', theaters[0]); } catch (_) {}
      }
      message(this, 'Loading showtimes…');

      // Coming soon always reads both theaters so it can tell where each movie is booked.
      const load = mode === 'soon' ? Object.keys(THEATER_NAMES) : theaters;
      Promise.all(load.map(function (t) {
        // The other theater's feed is only a helper here, so don't fail without it.
        return mode === 'soon' && theaters.indexOf(t) === -1 ? getFeed(base, t).catch(function () { return null; }) : getFeed(base, t);
      }))
        .then((feedsData) => {
          feedsData = feedsData.filter(Boolean);
          if (mode === 'soon') this.renderSoon(feedsData, theaters);
          else if (mode === 'movie') this.renderMovie(feedsData[0], params.get('film'));
          else this.renderNow(feedsData[0]);
        })
        .catch(() => message(this, 'Showtimes are not available right now. Please call the theater or try again soon.'));
    }

    renderNow(feed) {
      const films = feed.nowShowing || [];
      if (!films.length) { message(this, 'No showtimes are posted yet. Check back soon.'); return; }
      const todayKey = dayKey(new Date().toISOString());
      const days = Array.from(new Set(films.flatMap(function (f) { return f.sessions.map(function (s) { return dayKey(s.start); }); }))).sort().slice(0, 10);
      let picked = days[0];
      const tabs = el('div', { class: 'dt-days', role: 'group', 'aria-label': 'Pick a day' });
      const list = el('div', { class: 'dt-films' });
      const movieBase = this.dataset.moviePage || '/pages/movie';

      const draw = () => {
        tabs.replaceChildren.apply(tabs, days.map((key) => {
          const label = dayLabel(key, todayKey);
          const b = el('button', { type: 'button', class: 'dt-day' + (key === picked ? ' is-active' : ''), 'aria-pressed': key === picked ? 'true' : 'false' }, [
            el('span', { class: 'dt-day__top', text: label.top }),
            el('span', { class: 'dt-day__num', text: String(label.bottom) }),
          ]);
          b.addEventListener('click', function () { picked = key; draw(); });
          return b;
        }));
        const showing = films.filter(function (f) { return f.sessions.some(function (s) { return dayKey(s.start) === picked; }); });
        list.replaceChildren.apply(list, showing.map((f) => {
          const href = moviePageUrl(movieBase, feed.theater, f);
          const times = f.sessions.filter(function (s) { return dayKey(s.start) === picked; }).map(function (s) {
            return s.url
              ? el('a', { class: 'dt-time', href: s.url, text: timeLabel(s.start), 'aria-label': 'Buy tickets for ' + f.title + ' at ' + timeLabel(s.start) })
              : el('span', { class: 'dt-time is-disabled', text: timeLabel(s.start) });
          });
          return el('article', { class: 'dt-film' }, [
            poster(f, href),
            el('div', { class: 'dt-film__body' }, [
              el('a', { class: 'dt-film__title', href: href, text: f.title }),
              el('div', { class: 'dt-film__meta' }, [
                f.rating ? el('span', { class: 'dt-rating', text: f.rating }) : null,
                f.runtime ? el('span', { text: runtime(f.runtime) }) : null,
                f.trailer ? el('a', { href: href, class: 'dt-trailer-link', text: '▶ Watch trailer' }) : null,
              ]),
              el('p', { class: 'dt-eyebrow', text: 'PICK A TIME TO BUY TICKETS' }),
              el('div', { class: 'dt-times' }, times),
            ]),
          ]);
        }));
      };
      draw();
      this.replaceChildren(tabs, list);
    }

    renderSoon(feedsData, shown) {
      // Veezi shares one film list between both theaters, so every coming
      // soon movie shows up in both feeds. Each feed also says whether the
      // movie is on that theater's schedule, which decides where it is listed
      // and what the gold line says. The theme setting lists are a manual
      // override for movies booked before any showtimes are scheduled.
      const notBooked = {
        westside: titleSet(this.dataset.notWestside),
        stadium: titleSet(this.dataset.notStadium),
      };
      const byTitle = new Map();
      feedsData.forEach(function (feed) {
        (feed.comingSoon || []).forEach(function (f) {
          const k = f.title.trim().toLowerCase();
          if (notBooked[feed.theater] && notBooked[feed.theater].has(k)) return;
          if (!byTitle.has(k)) byTitle.set(k, {});
          byTitle.get(k)[feed.theater] = f;
        });
      });

      const films = [];
      byTitle.forEach(function (at) {
        const listed = Object.keys(at);
        const scheduledAt = listed.filter(function (t) { return at[t].scheduled; });
        // Once a movie is scheduled anywhere, only list it where it is scheduled.
        const where = (scheduledAt.length ? scheduledAt : listed).filter(function (t) { return shown.indexOf(t) !== -1; });
        if (!where.length) return;
        const dates = where.map(function (t) { return at[t].firstShow || at[t].opening; }).filter(Boolean)
          .sort(function (a, b) { return Date.parse(a) - Date.parse(b); });
        films.push(Object.assign({}, at[where[0]], {
          theater: where[0],
          date: dates[0] || at[where[0]].opening,
          where: scheduledAt.length ? scheduledAt : null,
        }));
      });

      const showWhere = shown.length > 1;
      films.sort(function (a, b) { return Date.parse(a.date) - Date.parse(b.date); });
      if (!films.length) { message(this, 'More movies are on the way. Check back soon.'); return; }
      const movieBase = this.dataset.moviePage || '/pages/movie';
      const limit = parseInt(this.dataset.limit || '10', 10);
      this.replaceChildren(el('div', { class: 'dt-soon' }, films.slice(0, limit).map(function (f) {
        const href = moviePageUrl(movieBase, f.theater, f);
        return el('a', { class: 'dt-soon__item', href: href }, [
          poster(f, null),
          el('span', { class: 'dt-soon__title', text: f.title }),
          el('span', { class: 'dt-soon__date', text: openingLabel(f.date) }),
          showWhere && f.where ? el('span', { class: 'dt-soon__where', text: whereLabel(f.where) }) : null,
        ]);
      })));
    }

    renderMovie(feed, filmId) {
      const all = (feed.nowShowing || []).concat(feed.comingSoon || []);
      const film = all.find(function (f) { return String(f.id) === String(filmId); });
      if (!film) { message(this, 'We couldn’t find that movie. It may have finished its run.'); return; }
      document.title = film.title + ' – ' + feed.theaterName;

      const yt = youtubeId(film.trailer);
      const trailer = yt
        ? el('div', { class: 'dt-trailer' }, [el('iframe', {
          src: 'https://www.youtube-nocookie.com/embed/' + yt + '?rel=0',
          title: film.title + ' trailer', allow: 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture', allowfullscreen: 'true', loading: 'lazy',
        })])
        : film.trailer ? el('p', {}, [el('a', { href: film.trailer, text: 'Watch the trailer' })]) : null;

      const todayKey = dayKey(new Date().toISOString());
      const byDay = {};
      film.sessions.forEach(function (s) { (byDay[dayKey(s.start)] = byDay[dayKey(s.start)] || []).push(s); });
      const dayRows = Object.keys(byDay).sort().map(function (key) {
        return el('div', { class: 'dt-dayrow' }, [
          el('span', { class: 'dt-dayrow__label', text: longDay(key, todayKey) }),
          el('div', { class: 'dt-times' }, byDay[key].map(function (s) {
            return s.url
              ? el('a', { class: 'dt-time dt-time--solid', href: s.url, text: timeLabel(s.start) })
              : el('span', { class: 'dt-time is-disabled', text: timeLabel(s.start) });
          })),
        ]);
      });

      this.replaceChildren.apply(this, [
        trailer,
        el('div', { class: 'dt-movie' }, [
          poster(film, null),
          el('div', { class: 'dt-movie__body' }, [
            el('h1', { class: 'dt-movie__title', text: film.title }),
            el('div', { class: 'dt-film__meta' }, [
              film.rating ? el('span', { class: 'dt-rating', text: film.rating }) : null,
              film.runtime ? el('span', { text: runtime(film.runtime) }) : null,
              !film.sessions.length && film.opening ? el('span', { text: openingLabel(film.opening) }) : null,
            ]),
            film.synopsis ? el('p', { class: 'dt-movie__synopsis', text: film.synopsis }) : null,
            el('h2', { class: 'dt-movie__heading', text: 'SHOWTIMES AT ' + feed.theaterName.toUpperCase() }),
            dayRows.length ? el('div', {}, dayRows) : el('p', { class: 'dt-message', text: 'Showtimes haven’t been posted yet.' }),
            dayRows.length ? el('p', { class: 'dt-note', text: 'Tapping a time opens ' + feed.theaterName + ' ticketing to pick seats and pay.' }) : null,
          ]),
        ]),
      ].filter(Boolean));
    }
  }

  if (!customElements.get('dt-showtimes')) customElements.define('dt-showtimes', DtShowtimes);
})();
